import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../shared/Icon';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
const FIRST_FOCUS = FOCUSABLE.split(', ')
  .flatMap((s) => [`.iter1-modal-body ${s}`, `.iter1-modal-actions ${s}`]).join(', ');

/**
 * Ask ResAI Iteration 1 modal (PRD-1621): 480px, focus moves in and is trapped, Escape, the
 * header's Close and the backdrop close it, and focus returns to whatever opened it.
 * `initialFocusRef` picks the first focus (Delete focuses Cancel); otherwise the first control
 * in the body or actions gets it, never Close (Shared Queries handoff 2).
 */
function Modal({ title, onClose, children, actions, initialFocusRef, width = 480 }) {
  const titleId = useId();
  const dialogRef = useRef(null);

  useEffect(() => {
    const opener = document.activeElement;
    const first = initialFocusRef?.current || dialogRef.current?.querySelector(FIRST_FOCUS);
    first?.focus();
    return () => {
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
    // Mount/unmount only: focus moves in once and goes back once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = Array.from(dialogRef.current?.querySelectorAll(FOCUSABLE) || []);
    if (!items.length) return;
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    if (e.shiftKey && document.activeElement === firstItem) {
      e.preventDefault();
      lastItem.focus();
    } else if (!e.shiftKey && document.activeElement === lastItem) {
      e.preventDefault();
      firstItem.focus();
    }
  };

  return createPortal(
    <div className="iter1-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={dialogRef}
        className="iter1-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ width }}
        onKeyDown={onKeyDown}
      >
        <div className="iter1-modal-head">
          <h2 id={titleId} className="iter1-modal-title">{title}</h2>
          <button type="button" className="iter1-modal-close" data-component="Icon Button - Small - Tertiary" aria-label="Close" onClick={onClose}>
            <Icon name="x" size={20} />
          </button>
        </div>
        <div className="iter1-modal-body">{children}</div>
        {actions && <div className="iter1-modal-actions">{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
